import { CommandLoading } from 'cmdk';
import { Check, ChevronsUpDown, Loader2, UserPlus } from 'lucide-react';
import { useMemo, useState } from 'react';

import { useAddFamilyContact } from '~entities/family';
import { useGetParentByStudio } from '~entities/parent/api/get-parent-by-studio';

import { CreateAndLinkGuardianDialog } from '~features/contacts/family/create-and-link-guardian-dialog';

import useDebounce from '~shared/hooks/useDebounce';
import { cn } from '~shared/shadcn/lib/utils';
import { Button } from '~shared/shadcn/ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '~shared/shadcn/ui/command';
import { Label } from '~shared/shadcn/ui/label';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '~shared/shadcn/ui/popover';

interface LinkGuardianSectionProps {
  familyId: string;
  studentId: string;
  /** GHL locationId for POST /parents (not Mongo studio._id). */
  locationId: string;
  linkedParentIds: Array<string>;
}

const parentLabel = (parent: {
  firstName?: string;
  lastName?: string;
  name?: string;
  email?: string;
}): string => {
  const fromParts = `${parent.firstName ?? ''} ${parent.lastName ?? ''}`.trim();
  const fromName = parent.name?.trim();
  // Prefer first+last — Parent.name can lag after portal renames.
  const named =
    fromParts.length > 0
      ? fromParts
      : fromName && fromName.length > 0
        ? fromName
        : '';
  if (named && parent.email) return `${named} · ${parent.email}`;
  if (named) return named;
  return parent.email ?? 'Unknown parent';
};

export const LinkGuardianSection = ({
  familyId,
  studentId,
  locationId,
  linkedParentIds,
}: LinkGuardianSectionProps) => {
  const addContact = useAddFamilyContact(studentId);
  const [openCombobox, setOpenCombobox] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedParentId, setSelectedParentId] = useState('');
  const [createOpen, setCreateOpen] = useState(false);
  const debouncedSearch = useDebounce(searchQuery, 300);

  const { data: parentsData, isFetching } = useGetParentByStudio({
    page: 1,
    limit: 25,
    search: debouncedSearch,
  });

  const linkedSet = useMemo(() => new Set(linkedParentIds), [linkedParentIds]);

  const parents = (parentsData?.data ?? []).filter(
    (parent) => !linkedSet.has(parent._id),
  );

  const selectedParent = parents.find(
    (parent) => parent._id === selectedParentId,
  );

  const linkSelected = () => {
    if (!selectedParentId) return;
    addContact.mutate(
      {
        familyId,
        parentId: selectedParentId,
        isBillingContact: false,
        communicationLevel: 'Full',
        portalAccessEnabled: true,
      },
      {
        onSuccess: () => {
          setSelectedParentId('');
          setSearchQuery('');
          setOpenCombobox(false);
        },
      },
    );
  };

  return (
    <div className='space-y-3 rounded-lg border border-dashed border-border bg-muted/20 p-4'>
      <div className='space-y-1'>
        <Label className='text-sm font-medium'>Link another guardian</Label>
        <p className='text-xs text-muted-foreground'>
          Search this studio by name, email, or phone, or create a new Parent
          login and link them in one step.
        </p>
      </div>

      <Popover open={openCombobox} onOpenChange={setOpenCombobox}>
        <PopoverTrigger asChild>
          <Button
            variant='outline'
            role='combobox'
            aria-expanded={openCombobox}
            className='w-full justify-between font-normal'
          >
            <span className='truncate'>
              {selectedParent
                ? parentLabel(selectedParent)
                : 'Search parents in this studio…'}
            </span>
            <ChevronsUpDown className='ml-2 size-4 shrink-0 opacity-50' />
          </Button>
        </PopoverTrigger>
        <PopoverContent
          className='w-[var(--radix-popover-trigger-width)] p-0'
          align='start'
        >
          <Command shouldFilter={false}>
            <CommandInput
              placeholder='Name, email, or phone'
              value={searchQuery}
              onValueChange={setSearchQuery}
            />
            <CommandList>
              {isFetching ? (
                <CommandLoading className='flex w-full items-center justify-center py-3'>
                  <Loader2 className='size-5 animate-spin' />
                </CommandLoading>
              ) : (
                <>
                  <CommandEmpty>
                    {debouncedSearch.trim().length === 0
                      ? 'Type to search parents in this studio.'
                      : 'No matching parent found. Create one below.'}
                  </CommandEmpty>
                  <CommandGroup>
                    {parents.map((parent) => (
                      <CommandItem
                        key={parent._id}
                        value={parent._id}
                        onSelect={(value) => {
                          setSelectedParentId(value);
                          setOpenCombobox(false);
                        }}
                      >
                        <Check
                          className={cn(
                            'mr-2 size-4',
                            selectedParentId === parent._id
                              ? 'opacity-100'
                              : 'opacity-0',
                          )}
                        />
                        <div className='min-w-0'>
                          <p className='truncate text-sm font-medium'>
                            {parent.name.trim().length > 0
                              ? parent.name
                              : `${parent.firstName} ${parent.lastName}`.trim()}
                          </p>
                          <p className='truncate text-xs text-muted-foreground'>
                            {[parent.email, parent.primaryPhone]
                              .filter(Boolean)
                              .join(' · ') || 'No contact details'}
                          </p>
                        </div>
                      </CommandItem>
                    ))}
                  </CommandGroup>
                </>
              )}
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>

      <div className='flex flex-col gap-2 sm:flex-row'>
        <Button
          className='sm:flex-1'
          disabled={!selectedParentId || addContact.isPending}
          onClick={linkSelected}
        >
          {addContact.isPending ? (
            <>
              <Loader2 className='mr-2 size-4 animate-spin' />
              Linking…
            </>
          ) : (
            'Link selected guardian'
          )}
        </Button>
        <Button
          type='button'
          variant='secondary'
          className='sm:flex-1'
          onClick={() => {
            setCreateOpen(true);
          }}
        >
          <UserPlus className='mr-2 size-4' />
          Create and link guardian
        </Button>
      </div>

      <CreateAndLinkGuardianDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        locationId={locationId}
        familyId={familyId}
        studentId={studentId}
      />
    </div>
  );
};
